import { useState } from "react";
import { useAuthStore } from "@/stores/authStore";

interface UserProfileCardProps {
  /** 사용자 컬러 */
  userColor: string;
  /** 컬러 변경 핸들러 */
  onColorChange?: (color: string) => Promise<boolean>;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 로그아웃 핸들러 */
  onLogout: () => void;
  /** 게스트 로그인 프롬프트 */
  onShowLoginPrompt: () => void;
  /** 필터 모드 */
  filterMode?: "all" | "mine" | "user";
  /** 필터 변경 핸들러 */
  onFilterChange?: (mode: "all" | "mine" | "user", userId?: string) => void;
  /** 숨김 여부 (VN 시나리오 중) */
  isHidden?: boolean;
}

// 빈티지 명함 스타일
const CARD_STYLE: React.CSSProperties = {
  background: "linear-gradient(145deg, #faf8f0 0%, #f0e8d8 50%, #e8dcc8 100%)",
  border: "1px solid #c4a87c",
  borderRadius: "4px",
  boxShadow: `
    0 3px 8px rgba(0,0,0,0.25),
    0 1px 3px rgba(0,0,0,0.15),
    inset 0 1px 0 rgba(255,255,255,0.5)
  `,
  fontFamily: "'Noto Serif KR', 'Georgia', serif",
};

/**
 * UserProfileCard - 빈티지 명함 스타일 프로필 카드
 */
export function UserProfileCard({
  userColor,
  onColorChange,
  isLoading,
  onLogout,
  onShowLoginPrompt,
  filterMode,
  onFilterChange,
  isHidden,
}: UserProfileCardProps) {
  const user = useAuthStore((state) => state.user);
  const isAnonymous = useAuthStore.getState().isAnonymous();

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [tempColor, setTempColor] = useState(userColor);

  if (isHidden) return null;

  return (
    <div
      style={{
        ...CARD_STYLE,
        width: "260px",
        padding: "14px 18px",
        display: "flex",
        gap: "14px",
        pointerEvents: "auto",
      }}
    >
      {isAnonymous ? (
        // 게스트 모드
        <div
          style={{
            width: "100%",
            textAlign: "center",
            padding: "10px 0",
          }}
        >
          <div
            style={{
              fontSize: "9px",
              color: "#999",
              letterSpacing: "2px",
              marginBottom: "8px",
            }}
          >
            VISITOR MODE
          </div>
          <div
            style={{ fontSize: "12px", color: "#666", marginBottom: "12px" }}
          >
            로그인하여 건물을 만들고
            <br />
            프로필을 관리하세요
          </div>
          <button
            onClick={onShowLoginPrompt}
            style={{
              padding: "7px 16px",
              fontSize: "11px",
              fontWeight: 600,
              background: "linear-gradient(135deg, #7c6a52 0%, #5a4a3a 100%)",
              border: "none",
              borderRadius: "3px",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            로그인 / 회원가입
          </button>
        </div>
      ) : (
        // 로그인 모드
        <>
          {/* 왼쪽: 프로필 */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              <div
                onClick={() => setShowColorPicker(!showColorPicker)}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: userColor,
                  boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${userColor}44`,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#2a2520",
                  }}
                >
                  {user?.username || "사용자"}
                </div>
                <div style={{ fontSize: "9px", color: "#999" }}>ZIP주인</div>
              </div>
            </div>

            {/* 컬러 피커 */}
            {showColorPicker && (
              <div
                style={{ display: "flex", gap: "6px", alignItems: "center" }}
              >
                <input
                  type="color"
                  value={tempColor}
                  onChange={(e) => setTempColor(e.target.value)}
                  style={{
                    width: "28px",
                    height: "22px",
                    border: "none",
                    cursor: "pointer",
                  }}
                />
                <button
                  onClick={async () => {
                    const success = await onColorChange(tempColor);
                    if (success) setShowColorPicker(false);
                  }}
                  disabled={isLoading}
                  style={{
                    padding: "3px 8px",
                    fontSize: "9px",
                    background: "#7c6a52",
                    border: "none",
                    borderRadius: "2px",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  저장
                </button>
              </div>
            )}

            <button
              onClick={onLogout}
              style={{
                alignSelf: "flex-start",
                padding: "4px 10px",
                fontSize: "9px",
                background: "transparent",
                border: "1px solid #c4a87c",
                borderRadius: "2px",
                color: "#8a7a6a",
                cursor: "pointer",
                marginTop: "auto",
              }}
            >
              로그아웃
            </button>
          </div>

          {/* 오른쪽: 필터 */}
          {onFilterChange && (
            <>
              <div style={{ width: "1px", background: "#d4c4a8" }} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "6px",
                  minWidth: "60px",
                }}
              >
                <div
                  style={{
                    fontSize: "8px",
                    color: "#999",
                    letterSpacing: "1px",
                    textAlign: "center",
                  }}
                >
                  FILTER
                </div>
                {(["all", "mine"] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => onFilterChange(mode)}
                    style={{
                      padding: "5px 6px",
                      fontSize: "9px",
                      background:
                        filterMode === mode || (!filterMode && mode === "all")
                          ? "#7c6a52"
                          : "transparent",
                      border: "1px solid #c4a87c",
                      borderRadius: "2px",
                      color:
                        filterMode === mode || (!filterMode && mode === "all")
                          ? "#fff"
                          : "#7c6a52",
                      cursor: "pointer",
                    }}
                  >
                    {mode === "all" ? "전체" : "내 것만"}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
